import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LANDING_PAGE_DEFAULTS, DEFAULT_GLOBAL_SETTINGS, LandingPageContent, GlobalSiteSettings } from "@/config/landingPageDefaults";

export const useLandingPageContent = (niche: string) => {
  return useQuery({
    queryKey: ["cms-landing", niche],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("value")
        .eq("key", `landing_${niche}`)
        .maybeSingle();

      if (error) {
        console.error("Error fetching landing content:", error);
      }

      // Return CMS content if exists, otherwise return defaults
      if (data?.value) {
        return data.value as unknown as LandingPageContent;
      }

      // Fallback to defaults
      return LANDING_PAGE_DEFAULTS[niche as keyof typeof LANDING_PAGE_DEFAULTS] || null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGlobalSettings = () => {
  return useQuery({
    queryKey: ["cms-global-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("value")
        .eq("key", "global_settings")
        .maybeSingle();

      if (error) {
        console.error("Error fetching global settings:", error);
      }

      if (data?.value) {
        return data.value as unknown as GlobalSiteSettings;
      }

      return DEFAULT_GLOBAL_SETTINGS;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useAllCMSContent = () => {
  return useQuery({
    queryKey: ["cms-all-content"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("key, value")
        .like("key", "landing_%");

      if (error) throw error;

      const content: Record<string, LandingPageContent> = {};
      data?.forEach((item) => {
        const niche = item.key.replace("landing_", "");
        content[niche] = item.value as unknown as LandingPageContent;
      });

      return content;
    },
  });
};
